<template>
  <div :class="$style.container">
    <div v-show="!showLoadingOverlay">
      <FlatActionAnchor
        :buttonStyles="[$style.actionLink]"
        href="javascript:void(0);"
        :iconComponent="this.$options.icons.back"
        target="_self"
        text="Back"
        @click="handleRedirect"
      />
      <div :class="$style.content">
        <div>
          <h2 :class="$style.title">
            {{ displayName }}
          </h2>
          <h4 :class="$style.title">
            Control your end user's set-up experience when configuring a new device.
            Supported on macOS systems 11 or later.
          </h4>
        </div>
        <MdmDefaultGroup
          ordinalNumber="1"
          :osType="$options.darwin"
          @change="enableMainAction"
        />
        <MdmWelcomeScreen
          :displayName="displayName"
          ordinalNumber="2"
          :osType="this.$options.darwin"
          @change="enableMainAction"
        />
        <MdmSetupAssistant
          :displayName="displayName"
          ordinalNumber="3"
          :osType="this.$options.darwin"
          @change="enableMainAction"
        />
        <MdmZeroTouchEnrollment
          ordinalNumber="4"
          :osType="$options.darwin"
          @change="enableMainAction"
        />
      </div>
      <PanelFooter
        :disabled="!isMainActionEnabled"
        :handleAction="handleAction"
        :loading="loading"
        :showCancelAction="false"
      />
    </div>
    <LoadingOverlay
      :showLoadingOverlay="showLoadingOverlay"
    />
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import { get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notify } from 'client/util';
import BackIcon from 'public/img/icons/back.svg?inline';
import FlatActionAnchor from 'client/components/Buttons/FlatActionAnchor.vue';
import LoadingOverlay from 'client/components/LoadingOverlay/LoadingOverlay.vue';
import MdmDefaultGroup from 'client/MDM/MdmDefaultGroup.vue';
import MdmSetupAssistant from 'client/MDM/MdmSetupAssistant.vue';
import MdmWelcomeScreen from 'client/MDM/MdmWelcomeScreen.vue';
import MdmZeroTouchEnrollment from 'client/MDM/MdmZeroTouchEnrollment.vue';
import ModalEventBus from 'components/Modal/ModalEventBus';
import PanelFooter from 'client/components/PanelFooter.vue';
import RouteGuardMixin from 'client/AdminPortal/router/guards/RouteGuardMixin';
import Util from 'client/util/Util';

const { error, success } = Constants.default;
const {
  ItemTableConsts, ItemTypeConsts, StoreModelMap, StoreModuleMap,
} = Constants;
const { parentOSType } = ItemTableConsts;
const { darwin } = parentOSType;

const displayName = 'Mac Zero-Touch Experience';
const mdmSettingsModel = StoreModelMap[ItemTypeConsts.mdmSettings];
const GroupCollection = StoreModuleMap[ItemTypeConsts.group];

export default {
  name: 'MdmWelcomeExperienceMainPanel',

  components: {
    LoadingOverlay,
    FlatActionAnchor,
    PanelFooter,
    MdmDefaultGroup,
    MdmSetupAssistant,
    MdmWelcomeScreen,
    MdmZeroTouchEnrollment,
  },

  darwin,

  inject: ['$validator'],

  icons: {
    back: BackIcon,
  },

  mixins: [
    RouteGuardMixin,
  ],

  routes: {
    mdm: '/#/mdm/apple',
  },

  data: () => ({
    isMainActionEnabled: false,
    itemHasBeenSaved: false,
    loading: false,
    displayName,
    showLoadingOverlay: true,
  }),

  notify,

  computed: {
    ...mapGetters(mdmSettingsModel, [
      'hasItemChanged',
    ]),

    ...mapState(mdmSettingsModel, [
      'item',
    ]),
  },

  mounted() {
    this.handleMounted();
  },

  methods: {
    ...mapActions(mdmSettingsModel, [
      'fetchMdmSettings',
      'updateDep',
    ]),

    ...mapActions(GroupCollection, [
      'fetchEntireCollection',
      'setFilter',
    ]),

    // Enable main action button if there are changes and no errors
    enableMainAction() {
      this.isMainActionEnabled = this.hasItemChanged && !this.v2Errors.any();
    },

    async handleAction() {
      this.itemHasBeenSaved = true;
      this.loading = true;

      if (this.hasItemChanged) {
        const adesMacos = get(this.item.mdmDevices[0], 'ades.macos', '');
        try {
          await this.updateDep({ ades: { macos: adesMacos } });
          this.handleRedirect();
          this.$options.notify(success, `${this.displayName} settings have been updated.`);
        } catch {
          const message = `Unable to save ${this.displayName} settings.`;
          this.$options.notify(error, message);
        }
      } else {
        this.handleRedirect();
      }

      this.loading = false;
    },

    handleContinue() {
      ModalEventBus.close();
      this.handleRedirect();
    },

    async handleMounted() {
      try {
        const filter = {
          name: 'type',
          operator: 'eq',
          value: ItemTypeConsts.systemGroup,
        };
        this.setFilter({ filter });

        await this.fetchEntireCollection();
        await this.fetchMdmSettings();
        this.showLoadingOverlay = false;
      } catch {
        this.showLoadingOverlay = false;
        const message = `Unable to get the ${this.displayName} settings.`;
        this.$options.notify(error, message);
      }
    },

    handleRedirect() {
      Util.redirect(this.$options.routes.mdm);
    },

    hasModelChanged() {
      return !this.itemHasBeenSaved && this.hasItemChanged;
    },
  },
};
</script>

<style module>
@import './css/certificateStyles.css';
@import '../css/flex.css';
@import '../css/sections.css';

@value (gradientDark1, gradientDark3, gradientLight2) from '~css/colors.css';
@value (lightShadow3) from '~css/shadows.css';

.actionLink.actionLink {
  background: gradientLight2;
  color: black !important;
  display: flex;
  justify-content: flex-start;
  padding-left: 7px;
}

.container.container {
  display: flex;
  flex-direction: column;
  font-size: 1rem;
  height: calc(100% - 65px);
  padding: 15px 15px 4.5rem;
}

.content {
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  width: auto;
}

.content .title {
  color: gradientDark2;
  display: block;
  margin: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.content h2.title {
  font-size: 1.6rem;
  font-weight: 700;
  padding: 25px 0 20px 8px;
}

.content h4.title {
  font-size: 1.3rem;
  font-weight: 500;
  padding: 0 0 20px 10px;
  white-space: normal;
}

.headingTitle {
  display: inline-block;
  padding-left: 10px;
}
</style>
